'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Fragment, ReactNode } from 'react'
import Icon, { type IconName } from '../../atoms/Icon'

interface Props {
  placeholder?: number,
  items: {
    title: string
    href?: string;
    onClick?: () => void;
    icon?: IconName;
    iconNode?: ReactNode;
    dangerouslyRenderHtml?: boolean;
  }[]
  separator?: ReactNode;
}

const BreadCrumbs = ({ items, placeholder, separator }: Props) => {
  const pathname = usePathname()

  const renderIcon = (item: Props['items'][number]) => {
    if (item.iconNode) return <span className="mr-1.5 inline-flex items-center">{item.iconNode}</span>;
    if (item.icon) return <Icon name={item.icon} className="!size-4 fill-current mr-1.5" />;
    return null;
  };

  const defaultSeparator = <Icon name="chevron" className="!size-6 fill-t-tertiary -rotate-90" />;

  const renderSeparator = (key: string) => (
    <span key={key} className="inline-flex items-center text-t-tertiary">
      {separator || defaultSeparator}
    </span>
  );

  return (
    <div className="flex flex-row items-center pr-3">
      {
        placeholder
          ? Array.from({ length: placeholder }, (_, index) => (
            <div key={index} className="h-[25px] w-[110px] rounded-[5px] mr-2 bg-s-stroke2 animate-pulse" />
          ))
          : items.map((item, i) => (
            <Fragment key={item.title}>
              {
                item.onClick
                  ? (
                    <div onClick={item.onClick} key={item.title} className="inline-flex items-center">
                      <span className={`pr-1.5 text-body-2 font-semibold cursor-pointer transition-colors hover:text-t-primary ${item.href === pathname ? 'text-t-primary' : 'text-t-tertiary'} ${item.iconNode ? 'inline-flex items-center' : ''}`}>
                        {renderIcon(item)}
                        {item.dangerouslyRenderHtml ? <span dangerouslySetInnerHTML={{ __html: item.title }} /> : item.title}
                      </span>
                    </div>
                  )
                  : (
                    <Link href={`${item?.href}`} key={item.title} className="inline-flex items-center">
                      <span className={`pr-1.5 text-body-2 font-semibold cursor-pointer transition-colors hover:text-t-primary ${item.href === pathname ? 'text-t-primary' : 'text-t-tertiary'} ${item.iconNode ? 'inline-flex items-center' : ''}`}>
                        {renderIcon(item)}
                        {item.dangerouslyRenderHtml ? <span dangerouslySetInnerHTML={{ __html: item.title }} /> : item.title}
                      </span>
                    </Link>
                  )
              }

              {i + 1 < items.length && renderSeparator(`${item.title}-chevron`)}
            </Fragment>
          ))
      }
    </div>
  )
}

export default BreadCrumbs
